import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ImportItemStatus, ImportType } from "types/Import";
import Button from "common/components/Button/Button";
import useRouter from "common/hooks/use-router";
import {
  ClipboardDocumentCheckIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ArchiveBoxIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import componentMapping from "../utils/componentMapping";

const getIconFromItem = (action: ImportItemStatus) => {
  switch (action) {
    case ImportItemStatus.IN_PROGRESS:
      return <ClipboardDocumentCheckIcon className="w-6 text-blue-500" />;
    case ImportItemStatus.IMPORTED:
      return <CheckIcon className="w-6 text-emerald-500" />;
    case ImportItemStatus.ERROR:
      return <ExclamationTriangleIcon className="w-6 text-red-500" />;
    case ImportItemStatus.TO_ARCHIVE:
      return <ExclamationCircleIcon className="w-6 text-yellow-500" />;
    case ImportItemStatus.ARCHIVED:
      return <ArchiveBoxIcon className="w-6 text-slate-500" />;
    case ImportItemStatus.CANCELED:
      return <XMarkIcon className="w-6 text-primary-500" />;
    default:
      return null;
  }
};

const getColorFromItem = (action: ImportItemStatus) => {
  switch (action) {
    case ImportItemStatus.IN_PROGRESS:
      return "border-blue-500 border-4 border-double";
    case ImportItemStatus.IMPORTED:
      return "border-emerald-500 border-4 border-double";
    case ImportItemStatus.ERROR:
      return "border-red-500 border-4 border-double";
    case ImportItemStatus.TO_ARCHIVE:
      return "border-yellow-500 border-4 border-double";
    case ImportItemStatus.ARCHIVED:
      return "border-slate-500 border-4 border-double";
    case ImportItemStatus.CANCELED:
      return "border-primary-500 border-4 border-double";
    default:
      return "border-slate-500 border-4 border-double";
  }
};

function ImportDetailItem({ item, type, onCancelImport, ...props }: any) {
  const { status } = item;
  const router = useRouter();
  const id = router.query.id as string;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const containerClassName = clsx(
    "flex px-4 py-4 mt-2 w-full relative bg-white rounded-lg border border-slate-150 transition-all",
    getColorFromItem(status?.toLowerCase() as ImportItemStatus)
  );

  const SpecificComponent = componentMapping[type];

  // -- Specific code here --

  const onValidateAndComplete = useCallback(
    (selectedItem: any) => {
      if (type === ImportType.ZIMS) {
        if (selectedItem.id) {
          navigate(`/imports/${id}/animal/${selectedItem.id}/edit`, {
            state: { selectedItem },
          });
        } else {
          navigate(`/imports/${id}/animal/add`, {
            state: { selectedItem },
          });
        }
      }
    },
    [id, navigate, type]
  );

  // ------------------------

  return (
    <div className={containerClassName}>
      {getIconFromItem(status?.toLowerCase() as ImportItemStatus)}

      {SpecificComponent ? (
        <SpecificComponent item={item} />
      ) : (
        <div>Type de composant inconnu</div>
      )}
      {(item.status === ImportItemStatus.IN_PROGRESS ||
        item.status === ImportItemStatus.TO_ARCHIVE) && (
        <div className="flex flex-col justify-end items-end text-xs lg:border-l lg:pl-4 lg:min-w-[22em] border-slate-150 gap-2">
          <div className="flex w-full items-center justify-end text-slate-500 h-full">
            <div>
              <div className="flex w-full gap-1 ">
                {item.status === ImportItemStatus.IN_PROGRESS && (
                  <Button
                    type="primary"
                    icon="CheckIcon"
                    title={t("actions.validateAndComplete")}
                    onClick={() => onValidateAndComplete(item)}
                  />
                )}
                {item.status === ImportItemStatus.TO_ARCHIVE && (
                  <Button
                    type="error"
                    icon="TrashIcon"
                    title={t("actions.archive")}
                    onClick={() => props.onDelete(item)}
                    confirmMessage={t("actions.confirmDelete")}
                  />
                )}

                <Button
                  type="neutral-line"
                  icon="XMarkIcon"
                  title={t("actions.cancel")}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCancelImport(item);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportDetailItem;
